<script setup lang="ts">
// The bell: the account's in-app notifications (trial, billing, devices,
// sync trouble, storage). Opening it marks them read.
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useCloudStore } from '@/stores/cloud'
import { relativeTime } from '@/services/cloud'
import StatusBadge from './StatusBadge.vue'
import { Notification03Icon } from '@/components/icons'

const store = useCloudStore()
const router = useRouter()
const open = ref(false)
const root = ref<HTMLElement | null>(null)

function toggle() {
  open.value = !open.value
  if (open.value && store.unread > 0) setTimeout(() => store.markAllRead(), 1200)
}
function go(link: string | null) {
  open.value = false
  if (link) router.push(link)
}
function outside(e: MouseEvent) {
  if (root.value && !root.value.contains(e.target as Node)) open.value = false
}
onMounted(() => document.addEventListener('click', outside))
onBeforeUnmount(() => document.removeEventListener('click', outside))
const tone = (s: string) => (s === 'error' ? 'danger' : s === 'warning' ? 'warning' : s === 'success' ? 'success' : 'info') as 'danger' | 'warning' | 'success' | 'info'
const toneLabel = (s: string) => ({ error: 'Problem', warning: 'Heads up', success: 'Done', info: 'Info' })[s] ?? 'Info'
</script>

<template>
  <div ref="root" class="relative">
    <button class="icon-btn relative" :aria-label="store.unread ? `Notifications, ${store.unread} unread` : 'Notifications'" aria-haspopup="dialog" :aria-expanded="open" @click="toggle">
      <Notification03Icon />
      <span v-if="store.unread" class="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-[var(--cloud-deep)] text-white text-[10px] font-semibold flex items-center justify-center" aria-hidden="true">{{ store.unread > 9 ? '9+' : store.unread }}</span>
    </button>
    <div v-if="open" class="menu absolute right-0 top-full mt-1 w-[min(360px,calc(100vw-24px))] z-50 max-h-[70vh] overflow-y-auto" role="dialog" aria-label="Notifications">
      <div class="px-3 py-2 flex items-center">
        <p class="text-[13px] font-semibold flex-1">Notifications</p>
        <RouterLink to="/preferences" class="text-xs text-accent-text hover:underline" @click="open = false">Email settings</RouterLink>
      </div>
      <div class="menu-sep" />
      <p v-if="store.notifications.length === 0" class="px-3 py-6 text-center text-xs text-fg-3">Nothing new. Trial, billing, device and sync notices show up here.</p>
      <button v-for="n in store.notifications" :key="n.id" class="w-full text-left px-3 py-2 hover:bg-card-hover rounded-md flex flex-col gap-1" @click="go(n.link)">
        <div class="flex items-center gap-2">
          <StatusBadge :tone="tone(n.severity)" :label="toneLabel(n.severity)" />
          <span class="text-[11px] text-fg-3">{{ relativeTime(n.created_at) }}</span>
          <span v-if="!n.read_at" class="ml-auto text-[10px] font-semibold text-accent-text">NEW</span>
        </div>
        <p class="text-[13px] font-medium">{{ n.title }}</p>
        <p v-if="n.body" class="text-xs text-fg-2">{{ n.body }}</p>
      </button>
    </div>
  </div>
</template>
